import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Container, Typography, Box, Tabs, Tab, Skeleton, Button, Pagination, Stack,
    Dialog, DialogContent, IconButton
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag, faBoxOpen, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next'; // <--- Importante

// Contexto y Controladores
import UserContext from '../../../navigation/context/userContext';
import { getMyOrders } from '../../../controllers/customer/OrdersController'; 

// Componentes
import OrderCard from './OrderCard';
import CustomBreadcrumbs from '../../../components/breadcrumbs';
import MultiOrderPaymentContainer from '../../../components/payments/MultiOrderPaymentContainer';

export default function ClientOrdersPage() {
    const history = useHistory();
    const { t } = useTranslation(); // <--- Hook
    const { userData } = useContext(UserContext);
    
    // --- ESTADOS ---
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    
    // Filtros y Paginación
    const [activeTab, setActiveTab] = useState('all'); 
    const [page, setPage] = useState(1);               
    const [totalPages, setTotalPages] = useState(1);   

    // --- ESTADO PARA EL MODAL DE PAGO ---
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);

    // Cargar Pedidos
    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page]);

    const loadOrders = async () => {
        setLoading(true);
        window.scrollTo({ top: 0, behavior: 'smooth' }); 
        
        const res = await getMyOrders(activeTab, page);
        
        if (res.success) {
            setOrders(res.data);
            if (res.meta) {
                setTotalPages(res.meta.last_page);
            }
        }
        setLoading(false);
    };

    // --- MANEJADORES ---
    
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(1); 
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleViewDetails = (orderId) => {
        history.push(`/orders/${orderId}`);
    };

    const handlePayRetry = (order) => {
        setSelectedOrderForPayment(order);
        setPaymentModalOpen(true);
    };

    const handleClosePaymentModal = () => {
        setPaymentModalOpen(false);
        setSelectedOrderForPayment(null);
    };

    const handlePaymentSuccess = (successData) => {
        if (successData) {
            handleClosePaymentModal();
            loadOrders(); 
            alert(t('orders.payment_modal.success_alert'));
        }
    };

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <CustomBreadcrumbs returnTo='/profile' crumbs={[{ name: t('helpers.profile'), link: '/profile' }, { name: t('orders.title'), link: '/orders' }]} />
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
                <Typography variant="h4" fontWeight="bold">{t('orders.title')}</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<FontAwesomeIcon icon={faShoppingBag}/>} 
                    onClick={() => history.push('/')} 
                    sx={{ borderRadius: 50, px: 3 }}
                >
                    {t('orders.store_button')}
                </Button>
            </Box>

            {/* --- TABS --- */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {/* Usamos las claves de traducción */}
                    <Tab label={t('orders.tabs.all')} value="all" sx={{ fontWeight: 'bold' }} />
                    <Tab label={t('orders.tabs.pending')} value="pending" sx={{ fontWeight: 'bold' }} />
                    <Tab label={t('orders.tabs.active')} value="active" sx={{ fontWeight: 'bold' }} />
                    <Tab label={t('orders.tabs.history')} value="history" sx={{ fontWeight: 'bold' }} />
                </Tabs>
            </Box>

            {/* --- LISTA --- */}
            {loading ? (
                <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
                    ))}
                </Stack>
            ) : orders.length === 0 ? (
                <Box textAlign="center" py={8} bgcolor="#f9fafb" borderRadius={4} border="1px dashed #ddd">
                    <FontAwesomeIcon icon={faBoxOpen} size="3x" color="#ccc" />
                    <Typography variant="h6" color="text.secondary" mt={2}>
                        {t('orders.empty_state')}
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2}>
                    {orders.map((order) => (
                        <OrderCard 
                            key={order.id} 
                            order={order} 
                            onViewDetails={handleViewDetails}
                            onPayRetry={handlePayRetry}
                        />
                    ))}
                </Stack>
            )}

            {/* --- PAGINACIÓN --- */}
            {!loading && orders.length > 0 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={handlePageChange} 
                        color="primary" 
                        size="large"
                        shape="rounded"
                    />
                </Box>
            )}

            {/* --- MODAL DE PAGO --- */}
            <Dialog 
                open={paymentModalOpen} 
                onClose={handleClosePaymentModal}
                maxWidth="sm"
                fullWidth
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={0}>
                    <Typography variant="h6" fontWeight="bold">
                        {t('orders.payment_modal.title')}
                    </Typography>
                    <IconButton onClick={handleClosePaymentModal}>
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Box>

                <DialogContent>
                    {selectedOrderForPayment && (
                        <MultiOrderPaymentContainer 
                            paymentData={{
                                total_to_pay: selectedOrderForPayment.amount, 
                                orders: [selectedOrderForPayment],
                                reference: null 
                            }}
                            merchantUuid="QUD"
                            customer={{
                                nombre: userData.entity.name,
                                apellido: userData.entity.last_name,
                                documento_tipo: userData.entity.document_type,
                                documento: userData.entity.document_number,
                                correo:userData.user.email,
                                prefijo:userData.user.prefijo,
                                telefono: userData.entity.phone,
                                profesion: userData.entity.profession
                            }}
                            onPaymentSuccess={handlePaymentSuccess}
                        />
                    )}
                </DialogContent>
            </Dialog>

        </Container>
    );
}